import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CrmApiError, crmApi, type WaAccountView } from '../lib/crmApi.js';

/**
 * 工厂端多个 WhatsApp 号（09-23，一家最多 5 个）的共用逻辑。
 *
 * 「领空」= 现在看的是哪个号的客户。`''` = 全部号（聚合收件箱，默认）。
 * 选择写在网址上（`?wa=<号位 id>`）：点进客户再回来还在，发给同事的链接打开也是同一个号。
 * 网址上没有时用这台电脑上次的选择。
 */

const SCOPE_KEY = 'portal-wa-scope';

/**
 * 领空里除了各个号，还有一个「独立站表单」（10-06）：网站上留资的客户不是从哪个号进来的，
 * 但业务员要的是同一件事 ——「这一摊今天进了谁」。值不会跟号位 id（ObjectId）撞。
 */
export const SITE_SCOPE = 'site';

export function useWaAccounts() {
  const q = useQuery({
    queryKey: ['portal-wa-accounts'],
    queryFn: () => crmApi.waAccounts(),
    // 跟联系人列表同一个节奏：未读数字和跨号提醒靠它
    refetchInterval: 30_000,
    retry: false,
  });
  // 403 = 这家没开通 WhatsApp 托管（比如只走平台转单的联营工厂）—— 不按「没绑号」处理
  const notEnabled = q.error instanceof CrmApiError && q.error.status === 403;
  const accounts = q.data?.accounts ?? [];
  return {
    accounts,
    boundCount: q.data?.bound_count ?? 0,
    /** 线路（出口 IP）开通了没有：没开通要找我们，开通了工厂自己绑号 */
    lineReady: q.data?.line_ready ?? false,
    freeSlots: q.data?.free_slots ?? 0,
    max: q.data?.max ?? 5,
    loading: q.isLoading,
    /**
     * 进这一页之后拉到过新数据没有。缓存里的「0 个号」可能是扫码之前的旧数据，
     * 「没绑号就隐藏」要等进页后第一轮回来再下结论（之后每 30 秒的刷新不算，不然会闪）。
     */
    freshSinceMount: q.isFetchedAfterMount,
    /** 开通了托管、而且接口读到了 */
    enabled: !!q.data && !notEnabled,
    notEnabled,
    refetch: q.refetch,
  };
}

/**
 * 现在的领空。号位不存在了（被收回）就退回全部。
 *
 * `useStored: false` = 网址上没带就是全部，不拿这台电脑上次的选择（10-12，收件箱 #59）：
 * 从别处点进某个客户的链接（通知、客户页）不带 `?wa=`，套上上次的「独立站表单」之后，
 * 左栏是独立站客户、右边开着的却是 WhatsApp 客户 —— 业务员会以为这个客户的渠道标错了。
 */
export function useWaScope(
  accounts: WaAccountView[],
  opts: { useStored?: boolean } = {},
): [string, (id: string) => void] {
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get('wa');
  let stored = '';
  if (opts.useStored !== false) {
    try {
      stored = localStorage.getItem(SCOPE_KEY) ?? '';
    } catch {
      /* 无痕窗口读不了就算了 */
    }
  }
  const raw = fromUrl ?? stored;
  const scope =
    raw && (raw === SITE_SCOPE || accounts.some((a) => a.account_id === raw)) ? raw : '';

  const set = useCallback(
    (id: string) => {
      try {
        localStorage.setItem(SCOPE_KEY, id);
      } catch {
        /* 同上 */
      }
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) next.set('wa', id);
          else next.delete('wa');
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // 网址上没带、但记着上次的：补进网址，让之后的链接都带上
  useEffect(() => {
    if (!fromUrl && scope) set(scope);
  }, [fromUrl, scope, set]);

  return [scope, set];
}

/** 号的名字：备注名 > 号码 > WhatsApp N */
export function accountName(a: Pick<WaAccountView, 'label' | 'phone_number' | 'slot'>): string {
  return a.label?.trim() || a.phone_number || `WhatsApp ${a.slot}`;
}

/**
 * 联系人行上的号标签（10-06）：「官方号 ··7120」/「扫码号 ··3693」。
 * 备注名（「Strand 美国主号」）在一行小标签里看不出是哪种号、哪个号码 ——
 * 业务员要的是「这个客户从哪条线进来、回复会怎么发」，种类 + 尾号直接说清。
 */
export function accountKindTag(a: Pick<WaAccountView, 'kind' | 'phone_number' | 'slot'>): string {
  const kind = a.kind === 'cloud' ? '官方号' : '扫码号';
  const tail = phoneTail(a.phone_number);
  return tail ? `${kind} ${tail}` : `${kind} ${a.slot}`;
}

/** 号码只露后 4 位（「+64 ··6118」）：区分够用，截图外传也不整段暴露 */
export function phoneTail(phone: string | null | undefined): string {
  const d = String(phone ?? '').replace(/\D/g, '');
  if (!d) return '';
  return `··${d.slice(-4)}`;
}

export type AccountState = 'online' | 'reconnecting' | 'scanning' | 'offline' | 'unbound' | 'empty';

/**
 * 号的状态，给人看的那一层：
 *   online        连着
 *   reconnecting  断线正在自动重连（凭据还在，一般自己会好）
 *   scanning      正在出码 / 等扫
 *   offline       手机那边把我们踢了 / 连不上，要重新扫码
 *   unbound       工厂自己解绑了（历史还在，只能看）
 *   empty         空号位，从没绑过号
 */
export function accountState(a: WaAccountView): AccountState {
  switch (a.status) {
    case 'connected':
      return 'online';
    case 'reconnecting':
      return 'reconnecting';
    case 'qr_pending':
    case 'init':
      return 'scanning';
    case 'logged_out':
    case 'banned':
      return 'offline';
    default:
      return a.phone_number ? 'unbound' : 'empty';
  }
}

export const ACCOUNT_STATE_ZH: Record<AccountState, string> = {
  online: '在线',
  reconnecting: '重连中',
  scanning: '等待扫码',
  offline: '掉线',
  unbound: '已解绑',
  empty: '未绑定',
};

/** 按号码找号（列表行 / 会话抬头上的 inbound_to → 哪个号） */
export function useAccountByPhone(accounts: WaAccountView[]) {
  return useMemo(() => {
    const m = new Map<string, WaAccountView>();
    for (const a of accounts) {
      const d = String(a.phone_number ?? '').replace(/\D/g, '');
      if (d) m.set(d, a);
    }
    return (phone: string | null | undefined): WaAccountView | null =>
      m.get(String(phone ?? '').replace(/\D/g, '')) ?? null;
  }, [accounts]);
}

/** 「3 分钟前」—— 切换器每个号的最近消息时间 */
export function sinceText(at: string | null | undefined): string {
  if (!at) return '';
  const t = Date.parse(at);
  if (Number.isNaN(t)) return '';
  const mins = Math.max(0, Math.floor((Date.now() - t) / 60_000));
  if (mins < 1) return '刚刚';
  if (mins < 60) return `${mins} 分钟前`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h} 小时前`;
  return `${Math.floor(h / 24)} 天前`;
}
